<template>
    <div
        ref="promoBarRef"
        role="region"
        :aria-label="t('layout.promobar.regionLabel')"
        class="py-1 -mx-4 px-4 min-h-9 lg:h-9 flex flex-wrap flex-col text-lg"
        :class="[config.activeChristmasStyles ? 'bg-christmas border-t-2 border-secondary' : 'bg-hint']"
    >
        <div class="container m-auto">
            <div
                class="grid grid-cols-1 items-center gap-4 lg:grid-cols-[1fr_auto_1fr]"
                :class="[config.activeChristmasStyles ? 'text-white' : 'text-hint-content']"
            >
                <div
                    class="lg:flex lg:animate-none justify-center lg:justify-start"
                    :class="[isVisible(RATING) ? 'flex' : 'hidden', animationClass(RATING)]"
                >
                    <MiscTrustedShopsRating />
                </div>

                <div
                    class="lg:flex lg:animate-none justify-center text-center text-lg font-semibold"
                    :class="[isVisible(CENTER) ? 'flex' : 'hidden', animationClass(CENTER)]"
                >
                    <client-only>
                        <button
                            v-if="promobarData && (promobarData.title || ctaText)"
                            type="button"
                            :aria-label="promobarData.title ? undefined : ctaText ?? undefined"
                            aria-haspopup="dialog"
                            class="inline-flex flex-col md:flex-row md:gap-1 justify-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
                            @click="openModal"
                        >
                            <span v-if="promobarData?.title">{{ promobarData?.title }}</span>

                            <span v-if="ctaText" aria-hidden="true" class="hidden lg:inline-flex"> • </span>

                            <span v-if="ctaText" class="hidden lg:inline-flex items-center">
                                {{ ctaText }}
                                <MiscIcon name="chevron-right" size="md"/>
                            </span>
                        </button>
                    </client-only>

                    <MiscLink
                        v-if="!promobarData"
                        :to="centerUrl"
                        class="inline-flex items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
                    >
                        {{ centerText }}
                        <MiscIcon name="chevron-right" size="md"/>
                    </MiscLink>
                </div>

                <div
                    class="lg:flex lg:animate-none justify-center lg:justify-end"
                    :class="[isVisible(CONTACT) ? 'flex' : 'hidden', animationClass(CONTACT)]"
                >
                    <a
                        :href="contactUrl"
                        class="inline-flex items-center gap-1 whitespace-nowrap font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
                    >
                        <MiscIcon name="phone" size="md"/>
                        {{ contactText }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type {HblSlot} from '@/utils/types'
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useI18n} from 'vue-i18n'
import {usePromo, useSharedHeightMobileMenu, useTrustedShops} from '#imports'
import {storeToRefs} from "pinia";

const props = defineProps<{
    content: HblSlot,
}>()

const config = useRuntimeConfig().public
const {t} = useI18n()
const {fetchPromobarData, promobarData} = usePromo()

const {toggleModal} = useModal()

onMounted(async () => {
    await fetchPromobarData();
});

/**
 * Reads a static CMS element config value. Slots saved before these fields existed still hold
 * undefined, so every consumer needs an i18n fallback.
 */
const slotValue = function (key: string): string | null {
    const entry = props?.content?.config?.[key]

    if (entry != null && typeof entry === 'object' && 'value' in entry) {
        const value = (entry as { value: unknown }).value

        if (typeof value === 'string' && value.trim() !== '') {
            return value
        }
    }

    return null
}

const ctaText = computed(() => slotValue('ctaText'))
const centerText = computed(() => slotValue('centerText') ?? t('layout.promobar.centerText'))
const centerUrl = computed(() => slotValue('centerUrl') ?? t('layout.promobar.centerUrl'))
const contactText = computed(() => slotValue('contactText') ?? t('layout.promobar.contactText'))
const contactUrl = computed(() => slotValue('contactUrl') ?? t('layout.promobar.contactUrl'))

const openModal = async function () {
    toggleModal('promobar', promobarData?.value?.description)
}

const promoBarRef = ref<HTMLElement | null>(null)

const { sharedHeight } = storeToRefs(useSharedHeightMobileMenu())

/*
 * The bar now renders on every page, so its height has to be observed instead of being measured
 * once promo data arrives - FlyoutContextMenu sizes the mobile menu from this value.
 */
useResizeObserver(promoBarRef, ([entry]) => {
    sharedHeight.value = Math.round(entry.target.getBoundingClientRect().height)
})

/*
 * From lg all three items sit side by side. Below that there is only room for one, so they take
 * turns - the customer asked for the rating, then the promo, then the phone number.
 */
const RATING = 0
const CENTER = 1
const CONTACT = 2
const ROTATION_INTERVAL = 3000

const { rating } = useTrustedShops()

/*
 * Only items that actually have something to show take part. Center and contact always do (both
 * fall back to i18n), the rating only once Trusted Shops answered - rotating onto an empty item
 * would blank the bar for a full interval.
 */
const rotatingItems = computed<number[]>(() => (rating.value ? [RATING, CENTER, CONTACT] : [CENTER, CONTACT]))

const activeIndex = ref(0)
const isDesktop = useMediaQuery('(min-width: 1024px)')
const preferredMotion = usePreferredReducedMotion()
const prefersReducedMotion = computed(() => preferredMotion.value === 'reduce')
const isHovered = useElementHover(promoBarRef)
const { focused: isFocused } = useFocusWithin(promoBarRef)

/*
 * The motion preference only exists on the client. Branching on it during the first render would
 * disagree with the server output, and Vue then keeps the server DOM - which silently left reduced
 * motion users on whatever item was rendered first. So the switch waits until after hydration.
 */
const isMounted = ref(false)

onMounted(() => {
    isMounted.value = true
})

const activeItem = computed<number>(() => {
    if (isMounted.value && prefersReducedMotion.value) {
        return CENTER
    }

    return rotatingItems.value[activeIndex.value % rotatingItems.value.length]
})

/*
 * Cross fade, sequenced out then in: the outgoing item keeps its slot while it fades, and only
 * afterwards does the next one fade in. That way exactly one item is ever displayed, so the bar
 * height, the single tab stop and the accessibility tree all stay as they are without the fade.
 */
const FADE_OUT_DURATION = 250

const leavingItem = ref<number | null>(null)
const hasRotated = ref(false)
let leaveTimeout: ReturnType<typeof setTimeout> | undefined

watch(activeItem, (next, previous) => {
    if (previous === undefined || previous === next || prefersReducedMotion.value) {
        return
    }

    hasRotated.value = true
    leavingItem.value = previous

    clearTimeout(leaveTimeout)
    leaveTimeout = setTimeout(() => {
        leavingItem.value = null
    }, FADE_OUT_DURATION)
})

onUnmounted(() => {
    clearTimeout(leaveTimeout)
})

const displayedItem = computed<number>(() => leavingItem.value ?? activeItem.value)

const isVisible = function (item: number): boolean {
    return displayedItem.value === item
}

/** No animation before the first rotation, so the server markup needs no animation class either */
const animationClass = function (item: number): string {
    if (!hasRotated.value || prefersReducedMotion.value) {
        return ''
    }

    if (leavingItem.value !== null) {
        return item === leavingItem.value ? 'animate-fade-out' : ''
    }

    return item === activeItem.value ? 'animate-fade-in' : ''
}

const { pause, resume } = useIntervalFn(() => {
    activeIndex.value = (activeIndex.value + 1) % rotatingItems.value.length
}, ROTATION_INTERVAL, { immediate: false })

/** Paused on desktop as well - there all three are visible anyway, so ticking would be pure waste */
const shouldRotate = computed(() => !isDesktop.value
    && !prefersReducedMotion.value
    && !isHovered.value
    && !isFocused.value
    && rotatingItems.value.length > 1)

watch(shouldRotate, (rotate) => {
    if (rotate) {
        resume()
    } else {
        pause()
    }
}, { immediate: true })
</script>
